<template>
    <div>
    <div class="border border-gray-400 px-4 py-2 rounded" v-if="imageData">

        <div class="flex items-center cursor-pointer min-h-[60px] relative"
             :class="imageDropdownOpen ? 'mb-4' : ''"
             @click="imageDropdownOpen = !imageDropdownOpen"
        >
            <span v-if="imageDropdownOpen" class="flex-shrink-0 mr-2"><IconChevronUp/></span>
            <span v-else class="flex-shrink-0 mr-2"><IconChevronDown/></span>

            <span class="text-gray-500 flex-shrink-0 mr-2">
                <img :src="imageData.preview_url" alt="preview"
                     class="w-12 h-12 object-contain rounded border border-gray-200"/>
            </span>

            <div class="flex-1 min-w-0 relative">
                <h3 class="text-sm sm:text-base lg:text-lg font-semibold text-gray-700 truncate pr-8"
                    :title="imageData.file_name">{{ imageData.file_name }}</h3>
            </div>

            <button
                @click.stop="modalDeleteImageOpen = true"
                class="absolute right-0 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-red-500 p-1 z-10 bg-white rounded"
            >
                <IconTrash/>
            </button>
        </div>
        <div
            :style="{
            maxHeight: imageDropdownOpen ? 'none' : '0',
            opacity: imageDropdownOpen ? 1 : 0,
            pointerEvents: imageDropdownOpen ? 'auto' : 'none',
            overflow: imageDropdownOpen ? 'hidden' : 'hidden',
        }">

            <div v-if="limitColorExceeded" class="flex items-center space-x-2">
                <div class="flex items-center gap-3 bg-red-50 p-4 rounded-lg mb-4">
                    <div class="flex-shrink-0">
                        <div class="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                            <span class="text-white text-sm font-bold">!</span>
                        </div>
                    </div>
                    <span class="text-red-600 text-sm">{{ maxColorsMessage }}</span>
                </div>
            </div>
            <template v-if="printMethod && (printMethod.startsWith('L') || printMethod.startsWith('TC'))">
                <div class="flex items-center space-x-2 mb-4">
                    {{ substrateColorMessage }}
                </div>
            </template>
            <div
                class="flex items-center space-x-2 mb-4"
                v-else-if="!isRasterImage && !(printMethod && isDigitalPrint)"
            >
                <div class="relative w-full max-h-72 overflow-y-auto pr-1">
                    <div v-for="(defaultColor, index) in availableColors" :key="index" class="relative mb-4">
                        <div class="flex items-center space-x-2">
                            <button
                                @click="toggleDropdown(index)"
                                class="flex items-center justify-between flex-1 border border-gray-300 rounded p-2 text-sm"
                            >
                                <div class="flex items-center space-x-2">
                                    <div
                                        class="w-5 h-5 rounded-full border border-gray-300"
                                        :style="{ backgroundColor: selectedColors[index]?.hex || defaultColor.hex }"
                                    ></div>
                                    <span>{{ selectedColors[index]?.pantone || defaultColor.pantone }}</span>
                                </div>
                                <span v-if="openDropdowns[index]"><IconChevronUp/></span>
                                <span v-else><IconChevronDown/></span>
                            </button>
                            <button
                                v-if="availableColors.length > 1"
                                @click.stop="removeColor(index)"
                                class="flex-shrink-0 text-gray-500 hover:text-red-500 p-2 z-10 bg-white rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                :title="removeColorTooltip"
                                :disabled="colorOperationLoading"
                            >
                                <IconTrash/>
                            </button>
                        </div>

                        <div
                            v-if="openDropdowns[index]"
                            class="absolute left-0 z-50 mt-1 w-full bg-white border border-gray-300 rounded shadow-lg"
                        >
                            <ul>
                                <li
                                    @click="openColorModal(index)"
                                    class="flex items-center p-2 hover:bg-gray-100 cursor-pointer border-b border-gray-200"
                                >
                                    <div
                                        class="w-5 h-5 rounded-full border border-gray-300 mr-2 flex items-center justify-center"
                                    >
                                        <span class="text-gray-700 font-bold text-xs">+</span>
                                    </div>
                                    <span class="text-sm font-semibold text-black">{{ selectOtherColorText }}</span>
                                </li>
                                <li
                                    v-for="color in availableColors.filter(c => c.hex !== selectedColors[index]?.hex)"
                                    :key="color.hex"
                                    @click="selectColor(index, color)"
                                    class="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
                                >
                                    <div
                                        class="w-5 h-5 rounded-full border border-gray-300 mr-2"
                                        :style="{ backgroundColor: color.hex }"
                                    ></div>
                                    <span class="text-sm">{{ color.pantone }}</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            <div class="flex flex-col space-y-4 xl:flex-row xl:space-y-0 xl:space-x-2 xl:justify-between">
                <div class="flex flex-col">
                    <input
                        id="width"
                        v-bind:value="imageLayer.width"
                        @change="validateWidth"
                        type="number"
                        :placeholder="widthPlaceholder"
                        class="mt-1 w-full 2xl:w-32 px-2 py-1 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                    <label for="width">{{ widthLabel }}</label>
                </div>
                <div class="flex flex-col">
                    <input
                        id="height"
                        v-bind:value="imageLayer.height"
                        @change="validateHeight"
                        type="number"
                        :placeholder="heightPlaceholder"
                        class="mt-1 w-full 2xl:w-32 px-2 py-1 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                    <label for="height">{{ heightLabel }}</label>
                </div>
                <div class="flex flex-col">
                    <input
                        id="rotation"
                        v-bind:value="imageLayer.rotation"
                        @change="validateRotation"
                        type="number"
                        :placeholder="rotationPlaceholder"
                        class="hidden"
                        style="display: none;"
                    />
                    <input
                        id="rotation-display"
                        v-bind:value="displayRotation"
                        @change="validateDisplayRotation"
                        type="number"
                        max="360"
                        :placeholder="rotationPlaceholder"
                        class="mt-1 w-full 2xl:w-32 px-2 py-1 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                    <label for="rotation-display">{{ rotationLabel }}</label>
                </div>
            </div>
        </div>
    </div>

    <Modal
        v-model="modalDeleteImageOpen"
        :title="deleteImageTitle"
        :size="'sm'">
        <div
            class="flex items-center mb-4 mt-8"
        >
            <div
                class="text-sm mt-3"
            >
                <p>{{ deleteImageMessage }}</p>
            </div>
        </div>
        <div
            class="flex space-x-4 w-full mt-8"
        >
            <button
                class="w-1/2 px-4 py-2 bg-white border border-gray-300 rounded text-gray-700 hover:bg-gray-100"
                @click="modalDeleteImageOpen = false"
            >
                {{ deleteImageCancelText }}
            </button>
            <button
                class="w-1/2 px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-600"
                @click="removeImageLayer"
            >
                {{ deleteImageConfirmText }}
            </button>
        </div>
    </Modal>

    <Modal
        v-model="modalColorSelectOpen"
        :title="selectColorTitle"
        :size="'lg'">
        <div class="mb-4">
            <input
                v-model="colorFilterText"
                type="text"
                :placeholder="filterColorPlaceholder"
                class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            />
        </div>
        <div class="h-[60vh] overflow-y-auto">
            <div v-if="loadingColors" class="flex items-center justify-center py-8">
                <span class="text-gray-500">{{ loadingColorsText }}</span>
            </div>
            <div v-else-if="filteredColors.length === 0" class="flex items-center justify-center py-8">
                <span class="text-gray-500">{{ noColorsAvailableText }}</span>
            </div>
            <ul v-else class="space-y-1">
                <li
                    v-for="color in filteredColors"
                    :key="color.hex"
                    @click="selectedColorFromModal = color"
                    :class="[
                        'flex items-center p-3 hover:bg-gray-100 cursor-pointer rounded',
                        selectedColorFromModal?.hex === color.hex ? 'bg-blue-50 border-2 border-blue-500' : ''
                    ]"
                >
                    <div
                        class="w-8 h-8 rounded-full border border-gray-300 mr-3 flex-shrink-0"
                        :style="{ backgroundColor: color.hex }"
                    ></div>
                    <div class="flex-1">
                        <div class="font-semibold text-sm">{{ color.pantone }}</div>
                        <div class="text-xs text-gray-500">{{ color.hex }}</div>
                    </div>
                </li>
            </ul>
        </div>
        <div class="flex space-x-4 w-full mt-6 pt-4 border-t border-gray-200">
            <button
                class="w-1/2 px-4 py-2 bg-white border border-gray-300 rounded text-gray-700 hover:bg-gray-100"
                @click="modalColorSelectOpen = false; selectedColorFromModal = null"
            >
                {{ cancelButtonText }}
            </button>
            <button
                class="w-1/2 px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
                @click="confirmColorSelection"
                :disabled="!selectedColorFromModal || colorOperationLoading"
            >
                {{ changeButtonText }}
            </button>
        </div>
    </Modal>

    </div>
</template>

<script setup>
import {reactive, ref, computed, watch, onMounted, nextTick} from "vue";
import IconTrash from "../icons/IconTrash.vue";
import IconChevronUp from "../icons/IconChevronUp.vue";
import IconChevronDown from "../icons/IconChevronDown.vue";
import Modal from "../Modal.vue";
import {useTranslation} from '../../composables/useTranslation.js';
import IconOrangeQuestionMark from "../icons/IconOrangeQuestionMark.vue";

const props = defineProps({
    uuid: {
        type: String,
        required: false
    },
    imageData: {
        type: Object
    },
    removeLogo: {
        type: Number,
        default: 0, // Wartość domyślna
    },
    limitColorExceeded: {
        type: Boolean,
        default: false, // Wartość domyślna
    },
    colors: {
        type: Array,
        default: [], // Wartość domyślna
    },
    resizeLogo: {
        type: Object,
        default: null,
    },
    cropFileName: {
        type: String,
        default: null,
    },
    printMethod: {
        type: String,
        default: null,
    },
    forcedColor: {
        type: Object,
        default: null,
    },
    boundBoxValidator: {
        type: Function,
        default: null,
    },
    boundBoxFitter: {
        type: Function,
        default: null,
    },
    borderRotation: {
        type: Number,
        default: 0,
    },
})

const modalDeleteImageOpen = ref(false)
const modalColorSelectOpen = ref(false)
const maxColorsMessage = ref('')
const graverMessage = ref('')
const tloczenieMessage = ref('')
const substrateColorMessage = computed(() =>
    props.printMethod && props.printMethod.startsWith('TC')
        ? tloczenieMessage.value
        : graverMessage.value
)
const allColors = ref([])
const loadingColors = ref(false)
const colorOperationLoading = ref(false)
const selectedColorFromModal = ref(null)
const currentColorIndex = ref(null)
const colorFilterText = ref('')
const filterColorPlaceholder = ref('')

const filteredColors = computed(() => {
    if (!colorFilterText.value.trim()) {
        return allColors.value;
    }

    const filterLower = colorFilterText.value.toLowerCase().trim();
    return allColors.value.filter(color =>
        color.pantone && color.pantone.toLowerCase().includes(filterLower)
    );
});


const availableColors = computed(() => {
    let tmpColors = [];

    if (detectedColors && detectedColors.value && detectedColors.value.length > 0) {
        tmpColors = [...detectedColors.value]
    } else {
        tmpColors = [
            {name: 'Pure Black', pantone: 'Pure Black', hex: '#000000'},
            {name: 'Pure White', pantone: 'Pure White', hex: '#FFFFFF'},
        ];
    }

    let uniqueColors = [];
    tmpColors.forEach((color, index) => {

        if (!uniqueColors.find(tmp => tmp.pantone === color.pantone)) {
            uniqueColors.push(color);
        }
    });


    return uniqueColors;
});

const imageDropdownOpen = ref(true)
const scale = ref(null)
const updateFromConfigurator = ref(false)
const replaceColorImagePreview = ref(null)
const replaceColorImageName = ref(null)

const colorsDropdown = ref(false)

const {$t} = useTranslation()

// Reactive translations
const widthPlaceholder = ref('')
const widthLabel = ref('')
const heightPlaceholder = ref('')
const heightLabel = ref('')
const rotationPlaceholder = ref('')
const rotationLabel = ref('')
const selectOtherColorText = ref('')
const selectColorTitle = ref('')
const loadingColorsText = ref('')
const noColorsAvailableText = ref('')
const changeButtonText = ref('')
const cancelButtonText = ref('')
const removeColorTooltip = ref('Usuń kolor (ustaw jako transparentny)')
const deleteImageTitle = ref('Usuwanie grafiki')
const deleteImageMessage = ref('Czy na pewno chcesz usunąć grafikę z wizualizacji?')
const deleteImageCancelText = ref('Anuluj')
const deleteImageConfirmText = ref('Usuń')

const openDropdowns = ref([]);
// Przechowuje wybrane kolory dla każdego dropdown'a
const selectedColors = ref([]);
const detectedColors = ref([]);

const isRasterImage = computed(() => {
    if (!props.imageData || !props.imageData.file_name) {
        return false;
    }

    const isBitmapPdf = props.imageData.custom_properties?.is_bitmap_pdf === true
        || props.imageData.custom_properties?.is_bitmap_pdf === 1
        || props.imageData.custom_properties?.is_bitmap_pdf === '1';

    if (isBitmapPdf) {
        return true;
    }

    const rasterExtensions = ['png', 'jpg', 'jpeg', 'bmp', 'gif', 'tiff', 'tif', 'webp'];
    const fileName = props.imageData.file_name;
    const fileExtension = fileName.slice((fileName.lastIndexOf(".") - 1 >>> 0) + 1).toLowerCase();

    if (props.imageData.mime_type) {
        const rasterMimeTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/bmp', 'image/gif', 'image/tiff', 'image/webp'];
        if (rasterMimeTypes.includes(props.imageData.mime_type)) {
            return true;
        }
    }

    return rasterExtensions.includes(fileExtension);
});

const toggleDropdown = (index) => {
    // Zamknij wszystkie inne rozwijane listy
    Object.keys(openDropdowns).forEach(key => {
        if (parseInt(key) !== index) {
            openDropdowns.value[key] = false;
        }
    });

    // Przełącz stan konkretnego dropdown'a
    openDropdowns.value[index] = !openDropdowns.value[index];
};

const selectColor = async (index, color, isFromModal = false) => {
    if (colorOperationLoading.value) {
        return;
    }

    let oldColor = selectedColors.value[index];
    let newColor = color;

    // Zbierz WSZYSTKIE kolory o wybranym pantone wyjściowym
    const colorsToReplace = detectedColors.value.filter(
        (c) => c && c.pantone === oldColor.pantone
    );

    const formData = new FormData()
    formData.append('from', JSON.stringify(colorsToReplace))
    formData.append('to', JSON.stringify(newColor))
    formData.append('file_name', replaceColorImageName.value)
    formData.append('uuid', props.uuid)

    colorOperationLoading.value = true;
    try {
        const response = await fetch("/api/file/replace-color", {
            method: "POST",
            body: formData,
        }).then(response => {
            if (!response.ok) {
                throw new Error('Błąd podczas pobierania pliku');
            }

            replaceColorImageName.value = response.headers.get("Replace-Color-File-Name")

            return response.blob();
        }).then(blob => {
            const newUrl = URL.createObjectURL(blob);

            replaceColorImagePreview.value = newUrl;

            const pantoneToRemove = oldColor.pantone;
            const newPantone = newColor.pantone;

            if (isFromModal) {
                const newPantoneExists = detectedColors.value.some(
                    (c) => c && c.pantone === newPantone
                );

                if (newPantoneExists) {
                    for (let i = detectedColors.value.length - 1; i >= 0; i--) {
                        if (detectedColors.value[i] && detectedColors.value[i].pantone === pantoneToRemove) {
                            detectedColors.value.splice(i, 1);
                            selectedColors.value.splice(i, 1);
                            openDropdowns.value.splice(i, 1);
                        }
                    }
                } else {
                    for (let i = 0; i < detectedColors.value.length; i++) {
                        if (detectedColors.value[i] && detectedColors.value[i].pantone === pantoneToRemove) {
                            detectedColors.value[i] = {...newColor};
                        }
                    }

                    for (let i = 0; i < selectedColors.value.length; i++) {
                        if (selectedColors.value[i] && selectedColors.value[i].pantone === pantoneToRemove) {
                            selectedColors.value[i] = {...newColor};
                        }
                    }
                }
            } else {

                for (let i = detectedColors.value.length - 1; i >= 0; i--) {
                    if (detectedColors.value[i] && detectedColors.value[i].pantone === pantoneToRemove) {
                        detectedColors.value.splice(i, 1);
                        selectedColors.value.splice(i, 1);
                        openDropdowns.value.splice(i, 1);
                    }
                }
            }

            if (openDropdowns.value) {
                openDropdowns.value.forEach((_, idx) => {
                    openDropdowns.value[idx] = false;
                });
            }

            emit('update:replaceColorImage', replaceColorImagePreview.value, {
                colors: [...detectedColors.value],
                file: replaceColorImageName.value
            }, availableColors.value.length)

        });

    } catch (error) {
        console.error('Error replacing color:', error)
    } finally {
        colorOperationLoading.value = false;
    }
};

const openColorModal = async (index) => {
    currentColorIndex.value = index;
    selectedColorFromModal.value = null;
    colorFilterText.value = '';
    modalColorSelectOpen.value = true;

    if (allColors.value.length === 0) {
        await loadAllColors();
    }

    if (openDropdowns.value) {
        openDropdowns.value[index] = false;
    }
};

const loadAllColors = async () => {
    loadingColors.value = true;
    try {
        const response = await fetch("/api/file/all-colors");
        if (!response.ok) {
            throw new Error('Błąd podczas pobierania kolorów');
        }
        const data = await response.json();
        allColors.value = data.colors || [];
    } catch (error) {
        console.error('Błąd podczas ładowania kolorów:', error);
        allColors.value = [];
    } finally {
        loadingColors.value = false;
    }
};

const confirmColorSelection = async () => {
    if (!selectedColorFromModal.value || currentColorIndex.value === null || colorOperationLoading.value) {
        return;
    }

    const index = currentColorIndex.value;
    const color = selectedColorFromModal.value;

    modalColorSelectOpen.value = false;

    await selectColor(index, color, true);

    selectedColorFromModal.value = null;
    currentColorIndex.value = null;
};

const removeColor = async (index) => {
    if (colorOperationLoading.value) {
        return;
    }

    let oldColor = selectedColors.value[index];
    // Tworzymy obiekt koloru docelowego z 'TRANS' jako hex
    let newColor = {hex: 'TRANS', pantone: 'TRANSPARENT'};

    const colorsToReplace = selectedColors.value.filter(
        (c, i) => c && c.pantone === oldColor.pantone
    );

    const formData = new FormData()
    formData.append('from', JSON.stringify(colorsToReplace))
    formData.append('to', JSON.stringify(newColor))
    formData.append('file_name', replaceColorImageName.value)
    formData.append('uuid', props.uuid)

    colorOperationLoading.value = true;
    try {
        const response = await fetch("/api/file/replace-color", {
            method: "POST",
            body: formData,
        }).then(response => {
            if (!response.ok) {
                throw new Error('Błąd podczas pobierania pliku');
            }

            replaceColorImageName.value = response.headers.get("Replace-Color-File-Name")

            return response.blob(); // Przekształcenie strumienia na Blob
        }).then(blob => {
            // Tworzenie URL z Blob
            const newUrl = URL.createObjectURL(blob);

            // Wyświetlanie obrazu
            replaceColorImagePreview.value = newUrl;

            const pantoneToRemove = oldColor.pantone;

            for (let i = detectedColors.value.length - 1; i >= 0; i--) {
                if (detectedColors.value[i] && detectedColors.value[i].pantone === pantoneToRemove) {
                    detectedColors.value.splice(i, 1);
                    selectedColors.value.splice(i, 1);
                    openDropdowns.value.splice(i, 1);
                }
            }

            if (detectedColors.value.length === 0) {
                detectedColors.value = [];
                selectedColors.value = [];
                openDropdowns.value = [];
            }

            emit('update:replaceColorImage', replaceColorImagePreview.value, {
                colors: [...detectedColors.value],
                file: replaceColorImageName.value
            }, availableColors.value.length)

        });

    } catch (error) {
        console.error('Error removing color:', error)
    } finally {
        colorOperationLoading.value = false;
    }
};

const imageLayer = reactive({
    imageUrl: "",
    width: 100,
    height: 100,
    rotation: 0,
    colorOverlay: "#000000",
})

const colorOptions = [
    {
        name: 'Pure Black',
        hex: '#000000'
    },
    {
        name: 'Pure White',
        hex: '#FFFFFF'
    }
]


const fileInput = ref(null)


const triggerFileInput = () => {
    fileInput.value.click()
}

const getRealColors = () => {
    if (props.forcedColor) {
        return [{ hex: props.forcedColor.hex, pantone: props.forcedColor.pantone || props.forcedColor.name }];
    }
    return JSON.parse(JSON.stringify(availableColors.value));
}

const handleImageUpload = (event) => {
    const file = event.target.files[0]
    if (file) {
        const reader = new FileReader()
        reader.onload = (e) => {
            imageLayer.imageUrl = e.target.result
        };
        reader.readAsDataURL(file)
    }
}

const validateWidth = (event) => {

    const newValue = event.target.value; // Pobierz nową wartość z pola
    if (newValue < 1 || isNaN(newValue)) {
        event.preventDefault(); // Zatrzymaj domyślną akcję
        event.target.value = 1; // Ustaw wartość pola na 1
        imageLayer.width = 1; // Zaktualizuj model
    } else {
        imageLayer.width = newValue;
    }
};

const validateHeight = (event) => {

    const newValue = event.target.value; // Pobierz nową wartość z pola
    if (newValue < 1 || isNaN(newValue)) {
        event.preventDefault(); // Zatrzymaj domyślną akcję
        event.target.value = 1; // Ustaw wartość pola na 1
        imageLayer.height = 1; // Zaktualizuj model
    } else {
        imageLayer.height = newValue;
    }
};

const validateRotation = (event) => {
    updateFromConfigurator.value = false;
    const newValue = parseFloat(event.target.value);

    // Podstawowa walidacja zakresu
    if (newValue < -360 || newValue > 360 || isNaN(newValue)) {
        event.preventDefault();
        event.target.value = imageLayer.rotation;
        return;
    }

    // Jeśli mamy validator, sprawdź czy rotacja jest dozwolona
    if (props.boundBoxValidator) {
        const isValid = props.boundBoxValidator(newValue);

        if (!isValid) {
            event.preventDefault();
            event.target.value = imageLayer.rotation;
            return;
        }
    }

    imageLayer.rotation = newValue;
};

const displayRotation = computed(() => {
    const rotation = parseFloat(imageLayer.rotation) || 0;
    const borderRot = parseFloat(props.borderRotation) || 0;

    const relativeRotation = rotation - borderRot;

    if (relativeRotation < 0) {
        return relativeRotation + 360;
    }
    return relativeRotation;
});

// Normalizuje rotację do zakresu 0-360 (np. -30 -> 330, 400 -> 40)
const normalizeRotation = (value) => {
    let normalized = value % 360;
    if (normalized < 0) {
        normalized += 360;
    }
    return normalized;
};

const getCurrentDisplayRotation = () => {
    const rotation = parseFloat(imageLayer.rotation) || 0;
    const borderRot = parseFloat(props.borderRotation) || 0;
    const relativeRotation = rotation - borderRot;
    return relativeRotation < 0 ? relativeRotation + 360 : relativeRotation;
};

const validateDisplayRotation = (event) => {
    const rawValue = parseFloat(event.target.value);

    if (isNaN(rawValue)) {
        event.preventDefault();
        event.target.value = getCurrentDisplayRotation();
        return;
    }

    // Dowolną wartość (również ujemną lub > 360) sprowadzamy do zakresu 0-360
    const newValue = normalizeRotation(rawValue);
    event.target.value = newValue;

    let convertedValue = newValue;
    if (newValue > 180) {
        convertedValue = newValue - 360;
    }

    const borderRot = parseFloat(props.borderRotation) || 0;
    const absoluteRotation = borderRot + convertedValue;

    // Rotację z inputa zawsze aplikujemy w konfiguratorze (fitter obsługuje zarówno
    // obrót bez zmiany rozmiaru, jak i proporcjonalne zmniejszenie przy braku miejsca).
    // Rotację z inputa zawsze aplikujemy (0° jest poprawnym kątem — nie wolno
    // traktować wyniku fittera / samej wartości 0 jako falsy).
    if (typeof props.boundBoxFitter === 'function') {
        props.boundBoxFitter(absoluteRotation);
        updateFromConfigurator.value = true;
        imageLayer.rotation = absoluteRotation;
        nextTick(() => {
            updateFromConfigurator.value = false;
        });
        return;
    }

    // Fallback bez fittera – stara ścieżka przez watch → updateImageSettings
    if (!props.boundBoxValidator || props.boundBoxValidator(absoluteRotation)) {
        imageLayer.rotation = absoluteRotation;
        return;
    }

    event.preventDefault();
    event.target.value = getCurrentDisplayRotation();
};

const removeImageLayer = () => {
    modalDeleteImageOpen.value = false;
    imageLayer.imageUrl = ""
    emit('remove:imageLayer')
}

const isDigitalPrint = computed(() => {
    return props.printMethod && (
        props.printMethod.startsWith('H') ||
        props.printMethod.startsWith('D') ||
        props.printMethod.startsWith('FC') ||
        props.printMethod.startsWith('UV') ||
        props.printMethod.startsWith('PL') ||
        props.printMethod === 'TF3A' ||
        props.printMethod === 'SL'
    );
});


watch(
    imageLayer,
    (newConfig, oldConfig) => {
        if (updateFromConfigurator.value === false) {
            emit('update:imageLayer', newConfig)
        }

        updateFromConfigurator.value = false;
    },
    {deep: true},
)

watch(
    () => props.imageData,
    (newData) => {
        console.info(newData)
    },
    {deep: true},
)

watch(
    () => props.removeLogo, // Obiekt lub funkcja obserwowana
    (newValue, oldValue) => {
        removeImageLayer();
    },
    {deep: true},
);

watch(() => props.colors, (newColors, oldColors) => {
    if (isRasterImage.value) {
        detectedColors.value = [];
        selectedColors.value = [];
        openDropdowns.value = [];
        return;
    }

    if (newColors && newColors.colors && newColors.colors.length > 0) {
        const colors = [...newColors.colors];

        replaceColorImageName.value = newColors.file;
        detectedColors.value = colors

        const dropdowns = [];
        const selected = [];

        colors.forEach((color, index) => {
            selected[index] = {...color}; // Tworzymy kopię obiektu
            dropdowns[index] = false;
        });

        openDropdowns.value = dropdowns;
        selectedColors.value = selected;
    }
}, {
    deep: true,      // śledzi zmiany wewnątrz tablicy/obiektu
    immediate: true  // uruchamia callback od razu po inicjalizacji
})

watch(() => props.cropFileName, (newFileName, oldFileName) => {
    if (newFileName) {
        replaceColorImageName.value = newFileName;

    }
}, {
    deep: true,      // śledzi zmiany wewnątrz tablicy/obiektu
    immediate: true  // uruchamia callback od razu po inicjalizacji
})


watch(
    () => props.resizeLogo, // Obiekt lub funkcja obserwowana
    (newValue, oldValue) => {
        if (newValue) {
            updateFromConfigurator.value = true;
            if (scale.value === null) {
                scale.value = newValue.scale.dim / newValue.scale.line;
            }

            imageLayer.width = (scale.value * newValue.config.width * 10).toFixed(2);
            imageLayer.height = (scale.value * newValue.config.height * 10).toFixed(2);
            const nextRotation = parseFloat(newValue.config.rotation);
            imageLayer.rotation = Number.isFinite(nextRotation) ? Number(nextRotation.toFixed(2)) : 0;
            nextTick(() => {
                updateFromConfigurator.value = false;
            });
        }
    }
);

const emit = defineEmits([
    'remove:imageLayer',
    'update:imageLayer',
    'update:replaceColorImage',
])


defineExpose({
    getRealColors,
})


onMounted(async () => {
    widthPlaceholder.value = await $t('dimensions.width', 'Szerokość') + ' [mm]'
    widthLabel.value = await $t('dimensions.width', 'Szerokość') + ' [mm]'
    heightPlaceholder.value = await $t('dimensions.height', 'Wysokość') + ' [mm]'
    heightLabel.value = await $t('dimensions.height', 'Wysokość') + ' [mm]'
    rotationPlaceholder.value = await $t('dimensions.rotation', 'Rotacja') + ' [°]'
    rotationLabel.value = await $t('dimensions.rotation', 'Rotacja') + ' [°]'
    maxColorsMessage.value = await $t('colors.max_colors_message', 'Maksymalna liczba kolorów to :')
    graverMessage.value = await $t('colors.graver_message', 'GRAWER - kolor podłoża')
    tloczenieMessage.value = await $t('colors.tloczenie_message', 'TŁOCZENIE - kolor podłoża')
    selectOtherColorText.value = await $t('colors.select_other_color', 'WYBIERZ INNY KOLOR')
    selectColorTitle.value = await $t('colors.select_color_title', 'Wybierz kolor')
    loadingColorsText.value = await $t('colors.loading_colors', 'Ładowanie kolorów...')
    noColorsAvailableText.value = await $t('colors.no_colors_available', 'Brak dostępnych kolorów')
    changeButtonText.value = await $t('colors.change', 'Zmień')
    cancelButtonText.value = await $t('text.cancel', 'Anuluj')
    filterColorPlaceholder.value = await $t('colors.filter_placeholder', 'Filtruj po nazwie Pantone...')
    removeColorTooltip.value = await $t('colors.remove_color_tooltip', 'Usuń kolor (ustaw jako transparentny)')
    deleteImageTitle.value = await $t('image.delete_title', 'Usuwanie grafiki')
    deleteImageMessage.value = await $t('image.delete_from_visualization_message', 'Czy na pewno chcesz usunąć grafikę z wizualizacji?')
    deleteImageCancelText.value = await $t('image.cancel', 'Anuluj')
    deleteImageConfirmText.value = await $t('image.confirm_delete', 'Usuń')
})
</script>
